import {
  Button,
  colors,
  flexCenter,
  FlexColumn,
  FlexRow,
  font,
  fontFamilies,
  fullWidth,
  Input,
  Intent,
  marginHorizontal,
  noSelect,
  padding,
  paddingVertical,
  Spacing,
  standardSizes,
  TitledCard,
} from '@toilet-paper-games/ui';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential,
} from 'firebase/auth';

import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { BaseViewModel, useViewModelConstructor } from '@/common/utils/mobx/ViewModel';

import { ToastIntent } from '@/common/components/base/Toast';
import { ControllerAppModel } from '@/controller/models/ControllerAppModel';
import { Interpolation, Theme } from '@emotion/react';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FocusEventHandler } from 'react';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

const googleLogoUrl =
  'https://lh3.googleusercontent.com/COxitqgJr1sJnIDe8-jiKhxDx1FrYbtRHKJ9z_hELisAlapwE9LUPh6fcXIfb5vwpbMl4xl9H9TRFPc5NOO8Sb3VSgIBrfRYvW6cUA';

interface LoginFormViewModelProps {
  appStore: ControllerAppModel;
  onLoginSuccess: () => void;
}

interface LoginPageFormData {
  email: string;
  password: string;
}

const headingStyling: Interpolation<Theme> = [
  {
    // TODO: Add this color to the UI lib
    color: '#DDEFFF',
  },
  noSelect,
];

const buttonStyling: Interpolation<Theme> = [
  {
    padding: '10px',
    fontSize: 'min(5vw,20px)',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  paddingVertical('sm'),
];

const inputBadge: Interpolation<Theme> = [
  {
    position: 'absolute',
    right: '40px',
    cursor: 'pointer',
    color: '#DDEFFF',
    justifySelf: 'flex-start',
    overflow: 'hidden',
    fontSize: ' 26px',
  },
];

enum ToastFlags {
  Hide,
  Show,
}

class LoginPageViewModel extends BaseViewModel<LoginFormViewModelProps> {
  formData: LoginPageFormData = {
    email: '',
    password: '',
  };
  dirtyFormInputs = new Set<keyof LoginPageFormData>();
  showSettings = false;
  showPassword = false;
  loggingIn = false;
  loginFailed = false;
  loginError: string | null = null;
  passwordResetError: string | null = null;
  passwordResetSuccess = false;
  resettingPassword = false;
  resetPasswordRequestSent = false;

  constructor(props: LoginFormViewModelProps) {
    super(props);

    makeSimpleAutoObservable(this, {}, { autoBind: true });
  }

  get isValidEmail() {
    return this.testEmail(this.formData.email);
  }

  get canSendResetPasswordEmail() {
    return (
      this.passwordResetError === null && !this.passwordResetSuccess && this.isValidEmail
    );
  }

  onBlur(formInputKey: keyof LoginPageFormData): FocusEventHandler<HTMLInputElement> {
    return () => {
      const formInputValue = this.formData[formInputKey];
      if (!formInputValue) return;
      this.dirtyFormInputs.add(formInputKey);
    };
  }

  hasInputError(formKey: keyof LoginPageFormData): boolean {
    return {
      email: this.dirtyFormInputs.has(formKey) && !this.isValidEmail,
      password: this.dirtyFormInputs.has(formKey) && !this.formData[formKey],
    }[formKey];
  }

  testEmail(email: string) {
    return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email,
    );
  }

  get appStore() {
    return this.props.appStore;
  }

  toggleShowSettings(show?: boolean) {
    this.showSettings = show ?? !this.showSettings;
  }

  togglePasswordVisibility(show?: boolean) {
    this.showPassword = show ?? !this.showPassword;
  }

  setPassword(password: string) {
    this.formData.password = password;
  }

  setEmail(email: string) {
    this.formData.email = email;
  }

  backToLogin() {
    this.resettingPassword = false;
    this.resetPasswordRequestSent = false;
    this.passwordResetError = null;
    this.passwordResetSuccess = false;
  }

  async logInOAuthGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      // TODO: Move these values to a config file
      provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
      provider.setCustomParameters({ login_hint: 'user@example.com' });
      this.loggingIn = true;
      const auth = getAuth();
      const result = await signInWithPopup(auth, provider); // Firebase OAuth sign-in
      this.onAuthSuccess(result, 'Logged in user with OAuth:');
    } catch (err: any) {
      this.onAuthError(err);
    }
  }

  async loginEmailPassword() {
    try {
      this.loggingIn = true;
      const auth = getAuth();
      const { email, password } = this.formData;
      const result = await signInWithEmailAndPassword(auth, email, password); // if auth() doesn't work use firebaseAuth
      this.onAuthSuccess(result, 'Logged in user with email:');
    } catch (err: any) {
      this.onAuthError(err);
    }
  }

  async registerEmailPassword() {
    try {
      this.loggingIn = true;
      const auth = getAuth();
      const { email, password } = this.formData;
      const result = await createUserWithEmailAndPassword(auth, email, password);
      this.onAuthSuccess(result, 'Registered new user:');
    } catch (err: any) {
      this.onAuthError(err, ToastFlags.Show);
    }
  }

  private onAuthError(err: any, toastFlags: ToastFlags = ToastFlags.Hide) {
    this.loggingIn = false;
    this.loginFailed = true;
    const errorCode = err?.code;
    const errorMessage = err?.message;
    console.log(errorCode, errorMessage);
    if (!toastFlags) throw err;
    this.appStore.toastManager.addToast({
      title: errorCode ?? 'Authentication Error',
      description: errorMessage ?? 'An unknown error occurred',
      duration: 3000,
      intent: ToastIntent.ERROR,
    });
    throw err;
  }

  private onAuthSuccess(result: UserCredential, message: string) {
    const user = result.user;
    this.appStore.loggedInUser = user;
    console.log(message, user);
    this.props.onLoginSuccess();
  }

  async requestPasswordReset() {
    const auth = getAuth();
    if (!this.isValidEmail) return;
    try {
      this.resetPasswordRequestSent = true;
      await sendPasswordResetEmail(auth, this.formData.email);
      this.passwordResetSuccess = true;
    } catch (err: any) {
      this.resetPasswordRequestSent = false;
      this.passwordResetError = err?.message ?? 'An unknown error occurred';
    }
  }
}

interface LoginFormProps {
  appStore: ControllerAppModel;
  onLoginSuccess?: () => void;
}

const containerStyling: Interpolation<Theme> = [
  {
    height: 'fit-content',
    margin: -standardSizes.lg,
    width: `calc(100%-${standardSizes.lg}px)`,
  },
];

const errorBorder: Interpolation<Theme> = [
  {
    borderColor: colors.danger,
  },
];

export const LoginForm = observer((props: LoginFormProps) => {
  const { appStore, onLoginSuccess = () => {} } = props;
  const viewModel = useViewModelConstructor(LoginPageViewModel, {
    appStore,
    onLoginSuccess,
  });

  if (viewModel.resettingPassword) {
    return (
      <TitledCard css={containerStyling} alignment="center" title="Reset Password">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            viewModel.requestPasswordReset();
          }}
        >
          <FlexColumn
            css={[{ overflow: 'hidden' }, padding('md')]}
            justifyContent="center"
            alignItems="center"
            gap={standardSizes.lg}
          >
            <div css={fullWidth}>
              <p css={headingStyling}>EMAIL</p>
              <Input
                css={[fullWidth, viewModel.hasInputError('email') && errorBorder]}
                value={viewModel.formData.email}
                onInput={viewModel.setEmail}
                onBlur={viewModel.onBlur('email')}
              />
            </div>
            <span css={font(fontFamilies.secondary)}>
              {viewModel.passwordResetError !== null && (
                <span css={{ color: colors.danger }}>{viewModel.passwordResetError}</span>
              )}
              {viewModel.passwordResetSuccess && (
                <span css={{ color: colors.success }}>
                  Password reset instructions have been sent, check your email
                </span>
              )}
            </span>
            <FlexColumn gap={standardSizes.lg}>
              <Button
                buttonHeight={standardSizes.sm}
                shadow
                disabled={!viewModel.canSendResetPasswordEmail}
                css={buttonStyling}
                type="submit"
              >
                Reset Password
              </Button>
              <Button
                buttonHeight={standardSizes.sm}
                shadow
                onClick={viewModel.backToLogin}
                css={buttonStyling}
              >
                Back to login
              </Button>
            </FlexColumn>
          </FlexColumn>
        </form>
      </TitledCard>
    );
  }

  return (
    <TitledCard css={containerStyling} alignment="center" title="Sign In">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          viewModel.loginEmailPassword();
        }}
      >
        <FlexColumn
          css={[{ overflow: 'hidden' }, padding('md')]}
          justifyContent="center"
          alignItems="center"
          gap={standardSizes.lg}
        >
          <div css={fullWidth}>
            <p
              css={[
                headingStyling,
                viewModel.hasInputError('email') && { color: colors.danger },
              ]}
            >
              EMAIL
            </p>
            <Input
              css={[fullWidth, viewModel.hasInputError('email') && errorBorder]}
              value={viewModel.formData.email}
              onInput={viewModel.setEmail}
              onBlur={viewModel.onBlur('email')}
            />
          </div>
          <div css={fullWidth}>
            <p css={[headingStyling]}>PASSWORD</p>
            <FlexRow alignItems="center">
              <Input
                css={[fullWidth, viewModel.hasInputError('password') && errorBorder]}
                type={viewModel.showPassword ? 'text' : 'password'}
                value={viewModel.formData.password}
                onInput={viewModel.setPassword}
                onBlur={viewModel.onBlur('password')}
              />
              {viewModel.showPassword ? (
                <EyeSlash
                  onClick={() => viewModel.togglePasswordVisibility()}
                  css={inputBadge}
                />
              ) : (
                <Eye
                  onClick={() => viewModel.togglePasswordVisibility()}
                  css={inputBadge}
                />
              )}
            </FlexRow>
          </div>

          {viewModel.loginFailed && (
            <div
              css={[
                font(fontFamilies.secondary),
                {
                  color: colors.danger,
                  textAlign: 'center',
                },
              ]}
            >
              There was an issue signing you in
            </div>
          )}

          <div
            css={[
              font(fontFamilies.secondary),
              {
                color: colors.warning,
                textAlign: 'center',
              },
            ]}
          >
            Click{' '}
            <a
              css={{
                color: colors.primary,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={action(() => {
                viewModel.resettingPassword = true;
              })}
            >
              here
            </a>{' '}
            to reset your password
          </div>

          <Spacing />
          <FlexRow
            justifyContent="space-between"
            // ? Added row reverse to make the login button on the right AND be activated when the user presses enter
            // ! For some reason tabIndex isn't working for this
            css={[{ flexDirection: 'row-reverse' }, fullWidth]}
            gap={standardSizes.lg}
          >
            <Button
              buttonHeight={standardSizes.sm}
              shadow
              css={buttonStyling}
              type="submit"
              intent={Intent.Success}
              disabled={
                !viewModel.isValidEmail ||
                !viewModel.formData.password ||
                viewModel.loggingIn
              }
            >
              Sign In
            </Button>
            <Button
              buttonHeight={standardSizes.sm}
              shadow
              css={buttonStyling}
              onClick={viewModel.registerEmailPassword}
              disabled={
                !viewModel.isValidEmail ||
                !viewModel.formData.password ||
                viewModel.loggingIn
              }
            >
              Register
            </Button>
          </FlexRow>

          <Button
            onClick={viewModel.logInOAuthGoogle}
            css={[
              {
                fontSize: 'min(3vw,15px)',
              },
              flexCenter,
              padding('md'),
            ]}
            color={colors.componentHeaderBackground}
            buttonHeight={standardSizes.sm}
            shadow
          >
            <FlexRow justifyContent="space-around" alignItems="center">
              <span css={{ marginLeft: 12, textWrap: 'nowrap' }}>Use Google</span>
              <img
                css={[{ height: 20 }, marginHorizontal('lg')]}
                src={googleLogoUrl}
                alt="Google Logo"
              />
            </FlexRow>
          </Button>
        </FlexColumn>
      </form>
    </TitledCard>
  );
});
